<template>
  <div class="water-accounts-card">
    <!-- Card Header -->
    <div class="card-header">
      <div class="header-left">
        <div class="header-icon">
          <i class="bi bi-building"></i>
        </div>
        <div>
          <h3 class="card-title">Real Property Accounts</h3>
          <p class="card-subtitle">Linked real property accounts</p>
        </div>
      </div>
      <div class="header-right">
        <span class="badge accounts-count">{{ accounts.length }} account(s)</span>
      </div>
    </div>

    <!-- Accounts List -->
    <div v-if="accounts.length > 0" class="accounts-list">
      <div 
        v-for="account in accounts" 
        :key="account.account"
        class="account-item"
        :class="getAccountStatusClass(account)"
      >
        <div class="account-info">
          <div class="account-number">
            <div class="account-label">Tax Dec No.</div>
            <div class="account-value">{{ account.account }}</div>
          </div>
          
          <div class="account-name">
            <div class="account-label">Account Name</div>
            <div class="account-value">{{ account.account_name }}</div>
          </div>
          
          <div class="account-balance">
            <div class="account-label">Revision</div>
            <div class="account-value balance-amount" :class="{ 'negative': account.balance > 0 }">
              {{ account.revision }}
            </div>
          </div>
          
          <div class="account-status">
            <div class="account-label">Status</div>
            <div class="account-value">
              <span class="status-badge" :class="getStatusBadgeClass(account.status)">
                {{ account.status }}
              </span>
            </div>
          </div>
        </div>

        <div class="account-actions">
          <button 
            @click="$emit('view-bills', account)"
            class="btn-view-bills"
            :disabled="account.status === 'Disconnected'"
          >
            <i class="bi bi-receipt"></i>
            View Bills
          </button>
          
          <div class="additional-actions" v-if="showMoreActions">
            <button 
              @click="$emit('pay-bill', account)"
              class="btn-pay"
              :disabled="account.balance <= 0"
            >
              <i class="bi bi-cash-coin"></i>
              Pay
            </button>
            
            <button 
              @click="$emit('account-details', account)"
              class="btn-details"
            >
              <i class="bi bi-info-circle"></i>
              Details
            </button>
          </div>
          
          <button 
            @click="remove(account)"
            class="btn-more"
          >
            <i class="bi bi-trash"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <div class="empty-icon">
        <i class="bi bi-building"></i>
      </div>
      <h4>No Real Property Accounts</h4>
      <p>No real property accounts are linked to this user.</p>
      <button @click="$emit('add-account')" class="btn-add-account">
        <i class="bi bi-plus-lg"></i>
        Add Real Property Account
      </button>
    </div>

    <!-- Summary Footer -->
    <!-- <div v-if="accounts.length > 0" class="card-footer">
      <div class="footer-summary">
        <div class="summary-item">
          <span class="summary-label">Total Balance:</span>
          <span class="summary-value total-balance">
            ₱{{ formatCurrency(totalBalance) }}
          </span>
        </div>
        <div class="summary-item">
          <span class="summary-label">Active Accounts:</span>
          <span class="summary-value active-count">{{ activeAccountsCount }}</span>
        </div>
      </div>
      <button @click="$emit('manage-accounts')" class="btn-manage">
        <i class="bi bi-gear"></i>
        Manage Accounts
      </button>
    </div> -->
  </div>
</template>

<script setup>
import { computed } from 'vue'
//eslint-disable-next-line
const props = defineProps({
  accounts: {
    type: Array,
    default: () => []
  },
  showMoreActions: {
    type: Boolean,
    default: false
  }
})
//eslint-disable-next-line
const emit = defineEmits([
  'view-bills',
  'pay-bill',
  'account-details',
  'add-account',
  'manage-accounts',
  'remove'
])

// Computed properties
//eslint-disable-next-line
const totalBalance = computed(() => {
  return props.accounts.reduce((sum, account) => sum + (parseFloat(account.balance) || 0), 0)
})
//eslint-disable-next-line
const activeAccountsCount = computed(() => {
  return props.accounts.filter(account => account.status === 'Active').length
})

// Methods
//eslint-disable-next-line
const formatCurrency = (amount) => {
  if (!amount) return '0.00'
  return parseFloat(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const getAccountStatusClass = (account) => {
  return `status-${account.status?.toLowerCase() || 'unknown'}`
}

const getStatusBadgeClass = (status) => {
  const statusMap = {
    'Active': 'badge-active',
    'Disconnected': 'badge-disconnected',
    'Pending': 'badge-pending',
    'Suspended': 'badge-suspended',
    'Overdue': 'badge-overdue'
  }
  return statusMap[status] || 'badge-unknown'
}

const remove = (account) => {
  emit('remove',account)
}
</script>

<style scoped>
.water-accounts-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
  overflow: hidden;
}

/* Card Header - Red Theme */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-icon {
  font-size: 2rem;
  opacity: 0.9;
}

.card-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.card-subtitle {
  margin: 0.25rem 0 0 0;
  opacity: 0.9;
  font-size: 0.875rem;
}

.accounts-count {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

/* Accounts List */
.accounts-list {
  padding: 1rem;
}

.account-item {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  transition: all 0.3s ease;
}

.account-item:hover {
  border-color: #dc3545;
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.1);
}

.account-item.status-disconnected {
  opacity: 0.7;
  background: #f8f9fa;
}

.account-item.status-overdue {
  border-left: 4px solid #dc3545;
  background: #fff5f5;
}

.account-item.status-active {
  border-left: 4px solid #28a745;
}

.account-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.account-label {
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}

.account-value {
  font-size: 1rem;
  color: #2c3e50;
  font-weight: 500;
}

.balance-amount {
  font-weight: 600;
  font-size: 1.125rem;
}

.balance-amount.negative {
  color: #dc3545;
}

.balance-amount.positive {
  color: #28a745;
}

/* Status Badges */
.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-active {
  background: #d4edda;
  color: #155724;
}

.badge-disconnected {
  background: #e2e3e5;
  color: #383d41;
}

.badge-pending {
  background: #fff3cd;
  color: #856404;
}

.badge-suspended {
  background: #f8d7da;
  color: #721c24;
}

.badge-overdue {
  background: #f8d7da;
  color: #721c24;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  100% { opacity: 1; }
}

/* Account Actions - Red Theme */
.account-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px dashed #dee2e6;
}

.btn-view-bills {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  border: none;
  padding: 0.5rem 1.25rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(220, 53, 69, 0.3);
}

.btn-view-bills:hover:not(:disabled) {
  background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(220, 53, 69, 0.4);
}

.btn-view-bills:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 3px rgba(220, 53, 69, 0.3);
}

.btn-view-bills:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #6c757d;
  box-shadow: none;
}

.additional-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-pay {
  background: #28a745;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.btn-pay:hover:not(:disabled) {
  background: #218838;
  transform: translateY(-1px);
}

.btn-pay:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-details {
  background: transparent;
  color: #6c757d;
  border: 1px solid #dee2e6;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.btn-details:hover {
  background: #f8f9fa;
  border-color: #dc3545;
  color: #dc3545;
}

.btn-more {
  background: transparent;
  color: #6c757d;
  border: 1px solid #dee2e6;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.btn-more:hover {
  background: #f8f9fa;
  border-color: #dc3545;
  color: #dc3545;
}

/* Empty State - Red Theme */
.empty-state {
  text-align: center;
  padding: 3rem 2rem;
}

.empty-icon {
  font-size: 3rem;
  color: #dc3545;
  opacity: 0.3;
  margin-bottom: 1rem;
}

.empty-state h4 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.empty-state p {
  color: #6c757d;
  margin-bottom: 1.5rem;
}

.btn-add-account {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
}

.btn-add-account:hover {
  background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.4);
}

/* Card Footer - Red Theme */
.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}

.footer-summary {
  display: flex;
  gap: 2rem;
}

.summary-item {
  display: flex;
  flex-direction: column;
}

.summary-label {
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.summary-value {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
}

.total-balance {
  font-size: 1.125rem;
  color: #dc3545;
}

.active-count {
  color: #28a745;
}

.btn-manage {
  background: transparent;
  color: #dc3545;
  border: 1px solid #dc3545;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.btn-manage:hover {
  background: #dc3545;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(220, 53, 69, 0.3);
}

/* Hover effects for account items with red theme */
.account-item:hover .btn-view-bills:not(:disabled) {
  background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
}

.account-item:hover .btn-details {
  border-color: #dc3545;
  color: #dc3545;
}

.account-item:hover .btn-more {
  border-color: #dc3545;
  color: #dc3545;
}

/* Responsive Design */
@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
  
  .header-left {
    flex-direction: column;
    text-align: center;
  }
  
  .account-info {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .account-actions {
    flex-direction: column;
    gap: 1rem;
  }
  
  .additional-actions {
    width: 100%;
    justify-content: center;
  }
  
  .card-footer {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .footer-summary {
    flex-direction: column;
    gap: 1rem;
  }
}
</style>